import ThemeMgr, { DEFAULT_TOOLTIP_SHOW_DELAY } from '../../../gui/ThemeMgr';
import ListenerItem from '../../../utils/ListenerItem';
import Validator from '../../../utils/Validator';
import XtwUtils from './XtwUtils';

let TOOLTIP_SHOW_DELAY = -1;

/**
 * class XtwTooltipListenerExtension - provides the "hover tooltip" listeners and show-delay
 * timer for a table item ({@link XCellItem} or {@link XtwRowTemplateExtendedItem}). It is a
 * plain delegate: the host is passed in and accessed via {@link #host}. It extends
 * ListenerItem so it owns the mouse listeners, which bind to the host's element. When the
 * show-delay elapses it calls the host's own {@code onTooltipTimer} (which the concrete
 * hosts override to actually request/show the tooltip).
 */
export default class XtwTooltipListenerExtension extends ListenerItem {

	/**
	 * constructs a new instance
	 * @param {*} host the host table item
	 */
	constructor( host ) {
		super( 'widgets.xtw.util.XtwTooltipListenerExtension' );
		this._host = host;
		this.tooltipTimerTimeoutID = void 0;
		this.lastTooltipEvent = void 0;
		// the timer fires into the host's onTooltipTimer (overridden by the concrete hosts)
		this._onTooltipTimer = () => {
			const host = this.host;
			if ( Validator.isObject( host ) && Validator.isFunction( host.onTooltipTimer ) ) {
				host.onTooltipTimer();
			} else {
				this.onTooltipTimer();
			}
		};
		this._updateTtpTmo();
	}

	/**
	 * @returns {*} the host table item
	 */
	get host() {
		return this._host;
	}

	/**
	 * the host's DOM element. Exposed because this extension registers its own listeners
	 * (via {@link ListenerItem#addListener}), which bind to this element.
	 * @returns {HTMLElement|undefined} the host's DOM element
	 */
	get element() {
		return this.host.element;
	}

	/**
	 * @returns {Boolean} true if the host is rendered
	 */
	get isRendered() {
		return this.host.isRendered;
	}

	/**
	 * @override
	 */
	doDestroy() {
		this.clearTooltipTimer();
		this._onTooltipTimer = void 0;
		this._host = void 0;
		super.doDestroy();
	}

	_updateTtpTmo() {
		if ( TOOLTIP_SHOW_DELAY === -1 ) {
			let tmo = -1;
			const prop = ThemeMgr.getInstance().getProp( 'tooltipShowDelay' );
			if ( ( prop !== undefined ) && ( 'number' === prop.type ) ) {
				tmo = prop.value;
			}
			TOOLTIP_SHOW_DELAY = Validator.isPositiveInteger( tmo ) ? tmo : DEFAULT_TOOLTIP_SHOW_DELAY;
		}
	}

	addTooltipListeners( element = void 0 ) {
		this.addListener( "mouseenter", "onMouseEnter", element );
		this.addListener( "mouseover", "onMouseOver", element );
		this.addListener( "mouseleave", "onMouseLeave", element );
	}

	removeTooltipListeners( element = void 0 ) {
		this.clearTooltipTimer();
		this.removeListener( "mouseenter", element );
		this.removeListener( "mouseover", element );
		this.removeListener( "mouseleave", element );
	}

	onMouseEnter( evt, parameters = void 0 ) {
		this.clearTooltipTimer();
		this.initializeTooltipTimer();
		if ( Validator.is( evt, "MouseEvent" ) ) {
			evt.stopPropagation();
			this.lastTooltipEvent = evt;
		}
	}

	onMouseOver( evt, parameters = void 0 ) {
		if ( !Validator.isNumber( this.tooltipTimerTimeoutID ) ) {
			this.onMouseEnter( evt, parameters );
		} else if ( Validator.is( evt, "MouseEvent" ) ) {
			evt.stopPropagation();
			this.lastTooltipEvent = evt;
		}
	}

	onMouseLeave( evt, parameters = void 0 ) {
		if ( Validator.is( evt, "MouseEvent" ) ) {
			evt.stopPropagation();
		}
		this.clearTooltipTimer();
	}

	onTooltipTimer() {
		this.clearTooltipTimer();
	}

	initializeTooltipTimer() {
		if ( Validator.isNumber( this.tooltipTimerTimeoutID ) ) {
			return; // there is a timer already
		}
		this.tooltipTimerTimeoutID = setTimeout( this._onTooltipTimer, TOOLTIP_SHOW_DELAY );
	}

	clearTooltipTimer() {
		this.lastTooltipEvent = void 0;
		if ( !Validator.isNumber( this.tooltipTimerTimeoutID ) ) {
			return;
		}
		clearTimeout( this.tooltipTimerTimeoutID );
		this.tooltipTimerTimeoutID = void 0;
	}

	getCoordinateParameters( evt, element, parameters = {} ) {
		if ( !( element instanceof HTMLElement ) ) {
			element = this.host.element;
		}
		return XtwUtils.getCoordinateParameters( evt, element, parameters );
	}

}
